// City generation: buildable blocks, background skyline and star field.

import { W, H } from './canvas.js';
import { state } from './state.js';
import { isAttract } from './state.js';
import {
  FIELD_W,
  FIST_DX,
  FIST_DY,
  SKYLINE_COLUMNS,
  SKYLINE_COLUMN_WIDTH,
  SKYLINE_HEIGHT,
  SKYLINE_H_MAX,
  SKYLINE_H_MIN,
  STAR_COUNT,
} from './config.js';
import { isClassicMode, mods } from './modes.js';
import { WINDOW_FADE_MS, WINDOW_TICK_SECONDS, WINDOW_TOGGLE_CHANCE } from './config.js';
import { clamp } from './utils.js';
import { matchRand, reseed, seedFrom } from './rng.js';
import { spawnMoon } from './moon.js';
import { spawnCrate } from './crates.js';
import { resyncSplashScene } from './splash.js';
import { announceRound } from './render/announce.js';
import { fireBtn, loadAim, prepareSide, updateLabels } from './dom.js';
import { t } from './i18n.js';
import { draw } from './render/index.js';
import { playIntro } from './audio.js';

const MIN_W = 55;
const MAX_W = 105;
const MIN_H = 105;
const MAX_H = 330;
/**
 * Las torres del centro, y la posibilidad de que un edificio sea una.
 *
 * Las alturas se sorteaban igual en todas partes, así que una ronda podía plantar una llanura
 * justo entre los dos gorilas y dejar vacío el medio del cuadro, que es donde se mira. Ahora
 * cada edificio tira su altura de siempre y además tira una vez más: si es de los altos. La
 * posibilidad no es la misma en todo el ancho —queda en nada en los bordes del mundo y es esto
 * en la mitad—, así que la ciudad se levanta hacia el centro. Y no es sólo cuestión de vista:
 * una torre en medio es la pared que hay que salvar con el tiro.
 *
 * Los dos tramos se solapan a propósito (una torre puede salir de 300 y un edificio normal de
 * 330, que es su tope): la ciudad sube hacia el centro sin que se note el escalón.
 */
const TOWER_CHANCE = 82;
const TOWER_MIN = 300;
const TOWER_MAX = 520;

/**
 * The earthquake's own numbers, in canvas pixels and radians per second.
 *
 * The rise is twice the fall on purpose: a building lifted out of the ground reads as the
 * ground moving, and the same building sunk the same distance reads as a hole in the street
 * that the skyline is not drawn for. Together they are more than a gorilla is tall, which is
 * the whole of the difficulty: nothing you learned on the last shot is true on the next one.
 */
const QUAKE_RISE = 26;
const QUAKE_SINK = 12;
const QUAKE_RATE = 1.1;
const QUAKE_RATE_SLOW = 2.7;
/**
 * How much those two speeds vary from building to building, as a fraction of them.
 *
 * The phase alone was not enough, and this is the half that took measuring to find: with one
 * shared pair of frequencies, two buildings whose phases land close move as one animal and STAY
 * that way for as long as the round lasts, because nothing ever pulls them apart. Simulated over
 * eight rounds, the two buildings the gorillas were standing on came out sixteen degrees apart
 * in one of them — a correlation of 0.94 between two roofs, which is what "the players' buildings
 * go in unison" looks like from the sofa. A quarter of a spread either way takes a pair that far
 * apart through a right angle in about two seconds, so a coincidence becomes a moment instead of
 * a whole round.
 */
const QUAKE_RATE_SPREAD = 0.25;

/** How far above the rooftop the gorillas stand. */
const PERCH_OFFSET = 25;
/** Clearance a perch is judged on: how far a shot has to travel to count as away. */
const SIGHT_PX = 190;
/** The angles a gorilla would actually aim along, and how finely they are sampled. */
const AIM_FROM = 20;
const AIM_TO = 80;
const AIM_STEP = 4;
/** How many buildings in from each edge are considered as a perch. */
const PERCH_SPAN = 4;

/**
 * Un revuelto de un número: de un índice, un valor entre 0 y 1, sin saltos fijos ni rejilla.
 *
 * No es azar de verdad y no le hace falta: lo único que tiene que cumplir es que dos índices
 * seguidos no caigan cerca, y eso un salto fijo no lo cumple y esto sí. Determinista a propósito
 * —el mismo número da siempre el mismo sitio, para que el cielo sea el mismo en las dos máquinas
 * de una partida— y ajeno a los dados de la ronda, que son de la ciudad y no del fondo.
 */
function revuelto(n) {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

// Static scenery, generated ONCE per round so nothing jitters.
export function makeSkyline() {
  state.skyline = [];
  for (let i = 0; i < SKYLINE_COLUMNS; i++) {
    state.skyline.push({ x: i * SKYLINE_COLUMN_WIDTH, h: matchRand(SKYLINE_H_MIN, SKYLINE_H_MAX) });
  }

  state.stars = [];
  // Hasta la primera torre del fondo, y no hasta un número escrito: la banda del cielo de las
  // estrellas tiene que acabar donde empieza el skyline, que es como acababa antes del mundo de
  // 1280 —y por lo que no había una franja de cielo vacía entre las dos—.
  const banda = H - SKYLINE_HEIGHT - SKYLINE_H_MAX;

  for (let i = 0; i < STAR_COUNT; i++) {
    state.stars.push({
      // Repartidas al revuelto y no a saltos fijos. Eran `i * 83 % W` e `i * 47 % 250`, que no es
      // azar sino una rejilla: medido, los huecos entre estrellas vecinas en x salían de sólo tres
      // medidas —9, 13 y 22— y el vecino más cercano de cada una estaba siempre a 45 o 48 píxeles,
      // en las 90. Con las motas de un píxel no se notaba; en cuanto se ven, el campo se lee como
      // un patrón de puntos, que es como se reportó.
      x: revuelto(i) * W,
      y: revuelto(i + 977) * banda,
      // Estas dos sí siguen siendo un reparto regular, y se quedan: el tamaño y el oro de una
      // estrella no dibujan ninguna figura, y cada cinco y cada siete sale una proporción estable
      // sin gastar un dado más.
      big: i % 5 === 0,
      gold: i % 7 === 0,
      ph: Math.random() * Math.PI * 2,
    });
  }
}
/**
 * The windows of one building: a 7x8 pane every 18x23, from the bake to the last frame
 * of the round. One enumeration, so the city as painted and the one window that turns
 * later cannot disagree about where the windows are.
 */
function eachWindow(w, h, fn) {
  for (let yy = 18; yy < h - 10; yy += 23) {
    for (let xx = 9; xx < w - 8; xx += 18) fn(xx, yy);
  }
}

/** Which of a building's windows it is painted with, and which ones it keeps. */
function baseLit(i, xx, yy) {
  return ((i * 13 + yy + xx) % 7) < 3;
}

/** One window, at its place in a building's own canvas. */
function paintWindow(b, xx, yy, lit) {
  b.fillStyle = lit ? '#e3b872' : '#2c3a5c';
  b.fillRect(xx, yy, 7, 8);
  if (lit) {
    b.fillStyle = 'rgba(227,184,114,.25)';
    b.fillRect(xx - 1, yy - 1, 9, 10);
  }
}

/** One window, part way between off and on. */
function paintWindowAt(b, grad, xx, yy, level) {
  // El muro primero, siempre: el resplandor es un píxel más ancho que el panel, y sin borrarlo
  // quedaría un anillo de luz vieja alrededor de una ventana que se está apagando.
  b.fillStyle = grad;
  b.fillRect(xx - 1, yy - 1, 9, 10);

  // Y el panel apagado debajo del dorado, mezclando sobre él: así el color pasa por el azul del
  // panel en vez de por un dorado translúcido sobre el muro, que es el color de la pared y haría
  // que el encendido empezara pareciendo una mancha. Con `level` a 1 esto deja exactamente lo que
  // deja `paintWindow(..., true)`, que es por donde tiene que acabar.
  b.fillStyle = '#2c3a5c';
  b.fillRect(xx, yy, 7, 8);

  if (level <= 0) return;

  b.fillStyle = `rgba(227, 184, 114, ${level})`;
  b.fillRect(xx, yy, 7, 8);
  b.fillStyle = `rgba(227, 184, 114, ${0.25 * level})`;
  b.fillRect(xx - 1, yy - 1, 9, 10);
}

/**
 * A stable 0..1 from a number, used as `hash01(<integer>)`.
 *
 * A murmur-style mix rather than the usual `sin` trick: the same cost, measured to hold
 * up over the small, evenly spaced sets of numbers a building's windows are, and the
 * sine version is only ever a hash by luck.
 */
function hash01(n) {
  let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}

/** Whether this window is one of the few that turns this time round. */
function windowTurns(i, xx, yy, epoch, chance) {
  return hash01(i * 977 + yy * 31 + xx + epoch * 131) < chance;
}

/** Whether a crater has already taken the wall this window is painted on. */
function cratered(b, xx, yy) {
  const wx = b.x + xx + 3.5;
  const wy = b.y + yy + 4;

  return state.craters.some(c => {
    const dx = wx - c.x;
    const dy = wy - c.y;
    const reach = c.r + 7;
    return dx * dx + dy * dy < reach * reach;
  });
}

/**
 * The neighbours.
 *
 * A building carries a baked canvas, so a window that changes is a window painted into
 * that canvas — and that is also why the crater test is here: the damage is painted on
 * top of the panes, so a light switched on underneath one would flood a hole that a
 * banana put there.
 *
 * Each building re-rolls on its own tick, staggered by its address, and then any one
 * window only has WINDOW_TOGGLE_CHANCE of being the one that changes. Con el tick de cuatro
 * segundos eso es una ventana por edificio cada seis o siete —medido contando los fundidos que
 * arrancan—, cuatro veces lo que había; y aun así se lee como «la ciudad no se mueve», porque lo
 * que cambia es un panel de siete por ocho píxeles de lienzo. Ver la nota de `WINDOW_TICK_SECONDS`.
 * Derived from the clock rather than counted frame by frame, so a pause changes nothing and
 * the whole thing is a function of where a building stands and what time it is.
 *
 * And what changes is not painted here. The change is written down and the frame after is
 * what turns the light on, over WINDOW_FADE_MS: a pane that goes from dark to lit between
 * one frame and the next is a flicker, and the same pane over four tenths of a second is
 * a neighbour. See paintWindowAt and advanceFades.
 */
export function advanceWindows() {
  const t = state.clock / 1000;
  // El escaparate va al MISMO ritmo que la partida, y es el suyo: aquí había un factor que
  // multiplicaba (ventanas el doble de a menudo fuera de la partida), y con el tick de partida
  // bajado a cuatro segundos la mitad son dos —un árbol de Navidad—. Un factor que multiplica se
  // come por delante lo que se baje en el valor, así que ya no hay factor.
  const tick = WINDOW_TICK_SECONDS;
  const chance = WINDOW_TOGGLE_CHANCE;

  state.buildings.forEach((b, i) => {
    const epoch = Math.floor(t / tick + hash01(i * 1013 + 7));
    if (epoch === b.winEpoch) return;

    // The first pass over a fresh city writes the epoch down and paints nothing: the
    // panes were just painted with this pattern, and repainting every window of every
    // building on the first frame of every round is work for a picture already right.
    const fresh = b.winEpoch === undefined;
    b.winEpoch = epoch;
    if (fresh || !b.bctx) return;

    eachWindow(b.w, wallHeight(b.h), (xx, yy) => {
      const was = baseLit(i, xx, yy);
      if (!windowTurns(i, xx, yy, epoch, chance)) return;
      if (!b.grad || cratered(b, xx, yy)) return;

      // Y no se pinta el cambio: se apunta, y lo que dura el encendido lo pinta el fotograma.
      b.fades.push({ xx, yy, from: was ? 1 : 0, start: state.clock });
    });
  });

  advanceFades();
}

/**
 * Las ventanas que están cambiando, un paso más allá en cada fotograma.
 *
 * Cada una vive en el edificio al que pertenece, y no en una lista de la ciudad, para que una
 * ronda nueva se las lleve por delante con la ciudad: un encendido que sobreviviera a su ronda
 * sería una ventana pintada en un muro que ya no está.
 */
function advanceFades() {
  state.buildings.forEach(b => {
    if (!b.fades.length || !b.bctx) return;

    b.fades = b.fades.filter(f => {
      // Un cráter que ha caído encima se queda con la pared: el agujero está pintado en el
      // lienzo del edificio, y una luz repintada ahí lo taparía.
      if (cratered(b, f.xx, f.yy)) return false;

      const k = (state.clock - f.start) / WINDOW_FADE_MS;
      if (k >= 1) {
        paintWindow(b.bctx, f.xx, f.yy, !f.from);
        return false;
      }

      paintWindowAt(b.bctx, b.grad, f.xx, f.yy, f.from ? 1 - k : k);
      return true;
    });
  });
}

/**
 * The earthquake, in the mode that asks for it.
 *
 * A building is moved whole — roof and all — rather than being made taller or shorter. It
 * carries a baked canvas with its windows and its craters already in it, so a building that
 * grew would have to be repainted every frame, while a building that is lifted is one
 * drawImage with a different y. It is also the earthquake the mode is selling: what is moving
 * is the ground the gorillas are standing on, and the whole skyline comes with it.
 *
 * Two sines per building at different speeds, each with a phase of its own taken from its
 * address and the match seed, so two machines sharing a seed build the same wobble, and so
 * the skyline does not read as one city on a single spring. The fall is smaller than the
 * rise: a building that sinks far into the ground is a hole in the street, which the city is
 * not drawn for.
 *
 * Everything that reads a building reads it as it stands, so nothing else has to know: the
 * banana's collision test, the fist the shot starts from and the machine's own solver all
 * ask state.buildings and get today's skyline. The gorillas are the one thing that has to be
 * carried by hand, and they are carried by their roof — a gorilla left behind would be a
 * gorilla standing in the air.
 */
export function advanceQuake() {
  if (!state.buildings.length || !state.players.length) return;

  // El modo manda, y en el escaparate se suma: la portada y los menús tiemblan siempre, porque es el
  // anuncio de lo que se juega. En la pausa no, que ahí hay una partida parada y manda su modo.
  const on = !!mods().quake || isAttract();
  const t = state.clock / 1000;
  // Per round, so every round shakes to its own rhythm, and from the match seed, so the
  // same duel rebuilt from a code shakes the same way.
  const seed = seedFrom(state.matchSeed + ':quake:' + state.round);

  state.buildings.forEach((b, i) => {
    const lift = on ? quakeLift(t, i, seed) : 0;
    b.y = b.restY - lift;
  });

  state.players.forEach(p => {
    const roof = state.buildings.find(bb => p.x >= bb.x && p.x <= bb.x + bb.w);
    p.y = p.restY - (roof ? roof.restY - roof.y : 0);
  });

  // And the crate is deliberately NOT carried here. A settled one asks again on its own frame
  // which roof is under it (see advanceCrate), because between two towers the answer changes as
  // they shake past each other — the one the earthquake used to write down at landing could be
  // left 25px under the neighbour, with an 18px crate inside a wall. The gorillas have no such
  // question: each is on a perch of its own and the buildings do not move sideways.
}

/**
 * How far a building is off the ground right now, in pixels. Positive is up.
 *
 * Two sines, a slow one and a fast one, and the building gets its own phase AND its own speed.
 *
 * The phase alone was not enough, and that is what was reported: with one shared pair of
 * frequencies, two buildings whose phases happened to land close moved as one animal for the
 * whole round — nothing was ever going to pull them apart. Simulated over eight rounds, the two
 * buildings the gorillas stand on came out sixteen degrees apart in one of them, a correlation
 * of 0.94 between two roofs, which is exactly "the players' buildings go in unison" seen from
 * the sofa. With a speed of its own a pair that starts close drifts out of step within a couple
 * of seconds, so the coincidence is a moment rather than a round.
 *
 * Both numbers come off the address and the match seed, so two machines sharing a code still
 * build the same wobble — the speed is hashed with a different multiplier from the phase so the
 * two cannot end up correlated with each other.
 */
function quakeLift(t, index, seed) {
  const phase = hash01(index * 4211 + seed) * Math.PI * 2;
  const speed = 1 + (hash01(index * 7919 + seed) - 0.5) * QUAKE_RATE_SPREAD;
  const wave = Math.sin(t * QUAKE_RATE * speed + phase) * 0.7
    + Math.sin(t * QUAKE_RATE_SLOW * speed + phase * 1.7) * 0.3;
  return wave * (wave >= 0 ? QUAKE_RISE : QUAKE_SINK);
}

/**
 * How many rows of wall each building carries below its own base.
 *
 * A building stands with its base exactly on the bottom edge of the canvas, which is
 * free and invisible while nothing moves it — and a hole the moment it is lifted: the
 * sky shows where the ground should be. The canvas is therefore taller than the
 * building, with the extra rows hanging off the canvas at rest. Whatever raises a
 * building (the earthquake, QUAKE_RISE above) shows wall that was always there instead
 * of a gap, so a raised building reads as a taller building.
 *
 * It has to stay off the play area on purpose: only b.w/b.h are ever cratered or
 * hit-tested (see crater() in effects.js), so the skirt is below the ground and out of
 * the game, and a crater at the very bottom of a building can still bite into it.
 */
const SKIRT = QUAKE_RISE + 8;

/**
 * The wall of a building, top to bottom: the building itself plus the skirt.
 *
 * Named because two places enumerate a building's windows — the bake and the one window
 * that turns later — and they have to walk the same rows in the same order or the city as
 * painted and the city as it changes stop agreeing. See eachWindow().
 */
function wallHeight(h) {
  return h + SKIRT;
}

// Buildings are pre-rendered onto their own canvas so bananas can punch real
// craters into them (see crater() in effects.js).
export function paintBuilding(w, h, i) {
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = wallHeight(h);
  const b = cv.getContext('2d');

  const grad = b.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, i % 2 ? '#212f4d' : '#262c49');
  grad.addColorStop(1, '#0f1528');
  b.fillStyle = grad;
  // Past the end of the gradient the last stop holds, so the skirt comes out as the
  // colour the wall already ended in: the building simply keeps going into the ground.
  b.fillRect(0, 0, w, wallHeight(h));

  b.fillStyle = '#3a4a72';
  b.fillRect(0, 0, w, 5);

  // The windows go down the whole wall, skirt included: a raised building shows the same
  // floors as the ones above it rather than a blank plinth, and by the same layout, so the
  // rows below the old baseline line up with the columns already there. Nothing is spent on
  // them in the modes that never lift a building — those rows are off the canvas anyway.
  eachWindow(w, wallHeight(h), (xx, yy) => paintWindow(b, xx, yy, baseLit(i, xx, yy)));

  if (i % 5 === 0) {
    b.fillStyle = i % 2 ? '#e084ab' : '#6fe0cd';
    b.fillRect(8, 8, w - 16, 4);
  }

  // Kept with the canvas: a window that turns off is covered with the wall it was cut
  // into, and the wall is this gradient.
  cv.grad = grad;

  return cv;
}

/** True when a point is inside a solid block. Buildings are solid from the roof down. */
function insideBuilding(bs, px, py) {
  const b = bs.find(q => px >= q.x && px <= q.x + q.w);
  return !!b && py >= b.y;
}

/**
 * Where a gorilla standing on this building actually ends up, and where that puts
 * its fist.
 *
 * A centred gorilla on a narrow building throws from over the roof next door: the
 * buildings are 55 to 105 wide and the fist is 30px in from the middle, so on the
 * narrow ones the launch point is past the edge. It is backed off until the fist
 * is back over its own roof, which is the difference between a blocked shot and a
 * banana that starts inside a wall.
 */
function perchOn(building, dir) {
  const margin = 8;
  const inner = dir > 0 ? building.x + building.w : building.x;
  let gx = building.x + building.w * 0.5;

  const fistX = () => gx + dir * FIST_DX;

  if (dir > 0 && fistX() > inner - margin) gx -= fistX() - (inner - margin);
  if (dir < 0 && fistX() < inner + margin) gx += (inner + margin) - fistX();

  return {
    x: clamp(gx, building.x + 10, building.x + building.w - 10),
    y: building.y - PERCH_OFFSET,
  };
}

/**
 * How much of the aim a perch leaves, as the number of directions that get clear
 * of the skyline. -1 means the fist is inside a wall, which is not a perch.
 *
 * This is the whole reason the perch is chosen rather than assumed. Buildings run
 * from 105 to 520 tall — the middle of the world grows towers, see TOWER_CHANCE —
 * so the one next door can be a 415px cliff, and a gorilla on the wrong side of
 * that step can aim along every angle there is and hit the building it is standing
 * next to forty-nine times out of fifty.
 */
function openAim(building, dir, bs) {
  const p = perchOn(building, dir);
  const fx = p.x + dir * FIST_DX;
  const fy = p.y + FIST_DY;

  if (insideBuilding(bs, fx, fy)) return -1;

  let clear = 0;

  for (let a = AIM_FROM; a <= AIM_TO; a += AIM_STEP) {
    const rad = a * Math.PI / 180;
    const dx = Math.cos(rad) * dir;
    const dy = -Math.sin(rad);

    let d = 1;
    while (d <= SIGHT_PX && !insideBuilding(bs, fx + dx * d, fy + dy * d)) d++;
    if (d > SIGHT_PX) clear++;
  }

  return clear;
}

/**
 * Picks the perch: the building nearest the edge of the field that leaves the
 * most to aim at. Deterministic on purpose — it reads the skyline and nothing
 * else, so a match code still rebuilds the same round on another machine, and the
 * buildings themselves are untouched.
 *
 * Se elige entre los edificios del CAMPO y no entre los del mundo (ver FIELD_W):
 * la ciudad que se sale del cuadro está ahí para mirarla, y un gorila plantado en
 * ella estaría tan lejos del otro que ninguna de las dos partes podría alcanzarle.
 * Las paredes que se le atraviesan a la puntería siguen siendo las del mundo
 * entero, que es lo que `openAim` recibe en `bs`.
 */
function choosePerch(bs, fromRight, dir) {
  const inset = (W - FIELD_W) / 2;
  const field = bs.filter(b => b.x + b.w > inset && b.x < W - inset);
  const half = Math.floor(field.length / 2) - 1;
  let best = null;
  let bestScore = -2;

  for (let i = 0; i < PERCH_SPAN; i++) {
    const index = fromRight ? field.length - 2 - i : 1 + i;
    // Never let the two perches cross each other into the middle of the block.
    if (index < 1 || index > field.length - 2) continue;
    if (!fromRight && index > half) break;
    if (fromRight && index < field.length - 1 - half) break;

    const b = field[index];
    const score = openAim(b, dir, bs);

    if (score > bestScore) {
      bestScore = score;
      best = b;
    }
  }

  return best || field[1] || bs[1];
}

/** Builds a gorilla, with the bookkeeping the idle antics need (see antics.js). */
function newGorilla(building, dir) {
  return {
    ...perchOn(building, dir),
    alive: true,
    antic: null,
    lastAntic: null,
    anticStarted: 0,
    nextAntic: 0,
    asleep: false,
    asleepAt: 0,
    startle: 0,
  };
}

/**
 * Puts both gorillas on their perches, honouring a swap.
 *
 * Exported because a swap has to do mid-round exactly what a new city does: exchange
 * the two of them and turn them round. Done through the same perch code rather than
 * by copying coordinates, because a perch is backed off until the gorilla's fist
 * clears its own roof — x/y borrowed from the other side would leave a fist hanging
 * over the edge, which is the very bug the perch selection was written to fix.
 */
export function placeGorillas() {
  const swap = !!state.swapped;

  // Which way each of them looks is state now rather than a function of the index,
  // so the drawing and the hit test turn round together and keep agreeing.
  state.facing = swap ? [-1, 1] : [1, -1];

  // Perch on whichever of the first few buildings from each edge leaves the most to
  // aim at, rather than blindly on the second one: the second one can be a stub
  // standing next to a tower, and a gorilla there cannot shoot anywhere.
  state.players = swap
    ? [
      newGorilla(choosePerch(state.buildings, true, -1), -1),
      newGorilla(choosePerch(state.buildings, false, 1), 1),
    ]
    : [
      newGorilla(choosePerch(state.buildings, false, 1), 1),
      newGorilla(choosePerch(state.buildings, true, -1), -1),
    ];

  // Where each of them stands with the ground still. A perch is the rooftop of a building
  // that may be lifted at this instant — a swap happens mid-round, in the middle of a
  // tremor — so the resting place is written down as "wherever I am, plus however far my
  // roof is lifted right now". advanceQuake() reads it back the same way round.
  state.players.forEach(p => {
    const b = state.buildings.find(bb => p.x >= bb.x && p.x <= bb.x + bb.w);
    p.restY = p.y + (b ? b.restY - b.y : 0);
  });
}

/** Builds a fresh city, places both gorillas and resets the round. */
export function makeCity() {
  // Every round is derived from the match seed and the round number, so a code
  // carrying those two things rebuilds this exact city and this exact wind on
  // another machine.
  reseed(seedFrom(state.matchSeed + ':' + state.round));

  // Any banana still up is dropped, which is what stops the old round's flight
  // now that the render loop owns the animation frames.
  state.projectile = null;
  // And any leap with it, or the gorilla would be left hanging in the air over
  // the next round's city until his own jump finished.
  state.leap = null;
  // Nor does a shield outlive the round it was raised in.
  state.shield = null;
  state.spring = null;
  state.mushroom = null;
  // Y el dron, que ahora lleva una bomba: uno de la ronda anterior entrando en la nueva no es
  // una imagen, es un arma que mata sin que nadie la haya llamado en esta ronda.
  state.drone = null;
  state.bomb = null;
  state.paintedFor = -1;
  state.paintedUntil = 0;
  // Nor the force field, and the gold goes with it: a new round with the last one's glow on
  // would be a gorilla transformed by something that is no longer happening.
  // (El brillo del superguerrero se limpiaba aquí. El poder se quitó —bloqueaba el turno— y con
  // él sus dos campos: ver SPECIALS en progress.js.)
  // Nor a sheep, and this is the one of them that matters: a sheep that outlived its round
  // would be walking across the next one's city, and its fuse would go off in the middle of
  // somebody else's turn. A walking bomb is the one thing here that cannot be left running
  // while the level underneath it is thrown away.
  state.sheep = null;

  state.buildings = [];
  let px = 0;
  while (px < W) {
    const w = matchRand(MIN_W, MAX_W);
    // Lo que pesa el centro en este edificio: 0 en los bordes del mundo, 1 en la mitad. Al
    // cuadrado, para que la subida sea del centro y no de los cuartos de al lado.
    const centre = 1 - Math.abs(px + w / 2 - W / 2) / (W / 2);
    const tower = matchRand(0, 100) <= Math.round(TOWER_CHANCE * centre * centre);
    const h = tower ? matchRand(TOWER_MIN, TOWER_MAX) : matchRand(MIN_H, MAX_H);
    const y = H - h;
    state.buildings.push({ x: px, w, h, y, restY: y, canvas: paintBuilding(w, h, state.buildings.length), bctx: null, fades: [] });
    px += w;
  }

  state.buildings.forEach(b => {
    b.bctx = b.canvas.getContext('2d');
    b.grad = b.canvas.grad;
    b.winEpoch = undefined;
  });

  placeGorillas();

  state.wind = matchRand(-mods().windMax, mods().windMax);
  // The round's weather, and the rest of what the card promises: "Lluvia, nieve y rachas". Snow has
  // been in the engine since the beginning — weather.js picks its flakes, its speed and its drift —
  // and no mode ever asked for it, so the card promised something that could not happen. Drawn
  // here, from the round's own seed and for the same reason the wind is: the two machines of a
  // match by code have to be looking at the same round.
  if (mods().precipitation) state.mods.precipitation = matchRand(0, 1) ? 'snow' : 'rain';
  state.turn = 0;
  // A new round opens on the aim of whoever is at this keyboard — not on player one's,
  // which is the same thing against the machine and in a game of two people, and is
  // somebody else's angle and power over a wire when this machine is seat one. The last
  // thing on the sliders may also have been the other side's shot, which is the other half
  // of the reason: `prepareSide()` is the side the controls belong to here.
  loadAim(prepareSide());
  state.turnStartedAt = state.clock;
  state.debris = [];
  state.fireballs = [];
  state.smoke = [];
  state.shockwaves = [];
  state.shake = 0;
  state.flash = 0;
  // Damage does not carry over between levels.
  state.craters = [];

  makeSkyline();

  // Last, deliberately. The generator is a sequence, so drawing the moon's numbers
  // earlier would shift every number after it — and a match code written before the
  // moon moved has to keep rebuilding its city exactly. Nothing below this line
  // reads the generator.
  spawnMoon();
  // And last of all the crate, for the same reason: nothing below reads the dice.
  spawnCrate();

  fireBtn.disabled = false;
  updateLabels();

  // The start of the match, as heard: the round called out in the manner of an arcade machine,
  // with the intro fanfare under it. Held back while there is nobody in the other seat — a duel
  // whose code has been sent and not answered has a board and no opponent, and calling a round to
  // an empty field is how the first build did it: a ROUND 1 and a fanfare over nobody, with the
  // turn clock already running down behind them. It goes up the moment somebody arrives, which is
  // where rivalArrived() plays it from.
  if (state.awaitingRival) state.callPending = true;
  else startMatchCall();

  // The opening scene keeps its own copy of where the gorillas stand, because it is
  // the thing moving them. Any rebuild of the city has to tell it, or it goes on
  // holding them at the rooftops of the city that has just been thrown away.
  resyncSplashScene();

  draw();
}

/**
 * The match starting, as heard.
 *
 * Out here rather than inline in makeCity because a board can be built for a match that has not
 * started: a duel whose code has been sent and not answered builds its city, puts the code up and
 * waits. The round is not called until somebody is there to play it — and neither is the fanfare,
 * which lands with the call in a match that is really beginning.
 */
export function startMatchCall() {
  // Only in the original, which is the only place it was ever heard. These beeps are the sound of
  // the machine the game came off, and the modern modes have a band for that job: running both at
  // once is two games talking over each other.
  //
  // Still guarded on the context existing, so the very first city — drawn before any user gesture
  // — does not try to open an AudioContext and earn a console warning for its trouble.
  if (state.audioCtx && isClassicMode()) playIntro();

  // And the round, in the manner of an arcade machine. Guarded on a match being live, so the very
  // first city — the one drawn behind the title screen before anybody has played — does not
  // announce a round of nothing.
  if (state.matchLive) announceRound(state.round);
}
